import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { Sparkles, ArrowLeft, Mail } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const BRAND = 'GUIRETAL';
const PARENT = 'NIMVEXA';

// Section identifiers used both for the sticky TOC anchor scroll and for
// data-testid selectors. Order matches the legal document structure.
const SECTIONS = [
    'responsible',
    'data',
    'usage',
    'ai',
    'ocr',
    'legal_basis',
    'retention',
    'security',
    'processors',
    'payments',
    'cookies',
    'rights',
    'candidates',
    'transfers',
    'changes',
    'contact',
];

// Small helper: render either a paragraph string, an array of paragraphs,
// or an array of bullet items depending on the shape returned by i18n.
const renderList = (items) =>
    Array.isArray(items) && items.length > 0 ? (
        <ul className="list-disc pl-6 my-3 space-y-1 text-[#4B5563]">
            {items.map((it, i) => (
                <li key={i}>{it}</li>
            ))}
        </ul>
    ) : null;

const renderParagraphs = (items) =>
    Array.isArray(items) && items.length > 0 ? (
        items.map((p, i) => (
            <p key={i} className="my-3 leading-relaxed text-[#4B5563]">
                {p}
            </p>
        ))
    ) : null;

export default function PrivacyPage() {
    const { t, i18n } = useTranslation();
    const [activeSection, setActiveSection] = useState(SECTIONS[0]);

    // SEO title + meta description swap (restore on unmount)
    useEffect(() => {
        document.title = t('privacy.seo_title');
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', t('privacy.seo_description'));
        return () => {
            // Restore the app-wide values in the CURRENT language (not a stale copy).
            document.title = i18n.t('app.document_title');
            if (meta) meta.setAttribute('content', i18n.t('app.document_description'));
        };
    }, [t, i18n.language]);

    // TOC active-section highlighter via IntersectionObserver
    useEffect(() => {
        const observers = [];
        SECTIONS.forEach((id) => {
            const el = document.getElementById(`section-${id}`);
            if (!el) return;
            const io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((e) => {
                        if (e.isIntersecting) setActiveSection(id);
                    });
                },
                { rootMargin: '-40% 0px -55% 0px', threshold: 0 },
            );
            io.observe(el);
            observers.push(io);
        });
        return () => observers.forEach((o) => o.disconnect());
    }, []);

    const toc = useMemo(
        () =>
            SECTIONS.map((id, idx) => ({
                id,
                number: idx + 1,
                title: t(`privacy.section_${id}_title`),
            })),
        [t],
    );

    return (
        <div className="min-h-screen bg-white" data-testid="privacy-page">
            {/* Nav — GUIRETAL branded */}
            <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[#E5E7EB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="privacy-nav-home">
                        <BrandLogo size={36} />
                        <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">{BRAND}</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher />
                        <Link to="/">
                            <Button variant="ghost" size="sm" data-testid="privacy-back-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                {t('privacy.back_home')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Page header */}
            <header className="bg-gradient-to-b from-[#F5F7FF] via-white to-white py-14 sm:py-20 px-4 sm:px-6 border-b border-[#E5E7EB]">
                <div className="max-w-4xl mx-auto">
                    <p className="text-sm font-semibold uppercase tracking-wider text-[#002FA7] mb-3">
                        {t('privacy.eyebrow', { parent: PARENT })}
                    </p>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] font-['Outfit'] tracking-tight leading-tight"
                        data-testid="privacy-title"
                    >
                        {t('privacy.title')}
                    </h1>
                    <p className="text-sm text-[#6B7280] italic mt-3" data-testid="privacy-last-updated">
                        {t('privacy.last_updated_label')} {t('privacy.last_updated_placeholder')}
                    </p>
                    <p className="mt-6 text-base sm:text-lg text-[#4B5563] leading-relaxed">
                        {t('privacy.intro', { brand: BRAND, parent: PARENT })}
                    </p>
                </div>
            </header>

            {/* Body: sticky TOC (desktop) + article */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                <div className="grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-14">
                    {/* Sticky Table of Contents (desktop only) */}
                    <aside className="hidden lg:block" data-testid="privacy-toc">
                        <div className="sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto pr-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-4">
                                {t('privacy.toc_title')}
                            </p>
                            <ol className="space-y-1 text-sm">
                                {toc.map(({ id, number, title }) => (
                                    <li key={id}>
                                        <a
                                            href={`#section-${id}`}
                                            data-testid={`privacy-toc-${id}`}
                                            className={`block px-3 py-2 rounded-md transition-colors ${
                                                activeSection === id
                                                    ? 'bg-[#002FA7]/10 text-[#002FA7] font-semibold'
                                                    : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                                            }`}
                                        >
                                            <span className="text-[#9CA3AF] mr-2">{String(number).padStart(2, '0')}</span>
                                            {title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </aside>

                    {/* Article */}
                    <article
                        className="max-w-3xl text-[#4B5563] leading-relaxed [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-[#0A0A0A] [&_h2]:font-['Outfit'] [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:tracking-tight [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#0A0A0A] [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:my-3 [&_ul]:my-3 [&_strong]:text-[#0A0A0A] [&_strong]:font-semibold"
                        data-testid="privacy-article"
                    >
                        {toc.map(({ id, number, title }) => (
                            <section
                                key={id}
                                id={`section-${id}`}
                                data-testid={`privacy-section-${id}`}
                                className="scroll-mt-24"
                            >
                                <h2>
                                    {number}. {title}
                                </h2>
                                {renderParagraphs(t(`privacy.section_${id}_p`, { returnObjects: true, defaultValue: [] }))}
                                {/* Subsections (only used by "data" — 4 subsections) */}
                                {id === 'data' && (
                                    <>
                                        {[1, 2, 3, 4].map((sub) => (
                                            <div key={sub} data-testid={`privacy-section-data-sub-${sub}`}>
                                                <h3>{t(`privacy.section_data_sub${sub}_title`)}</h3>
                                                {renderParagraphs(
                                                    t(`privacy.section_data_sub${sub}_p`, { returnObjects: true, defaultValue: [] }),
                                                )}
                                                {renderList(
                                                    t(`privacy.section_data_sub${sub}_list`, { returnObjects: true, defaultValue: [] }),
                                                )}
                                            </div>
                                        ))}
                                    </>
                                )}
                                {renderList(t(`privacy.section_${id}_list`, { returnObjects: true, defaultValue: [] }))}
                                {/* Highlighted callouts (per-section optional) */}
                                {t(`privacy.section_${id}_callout`, { defaultValue: '' }) && (
                                    <p className="my-4 border-l-4 border-[#002FA7] bg-[#F5F7FF] px-4 py-3 text-[#0A0A0A] font-medium">
                                        {t(`privacy.section_${id}_callout`)}
                                    </p>
                                )}
                                {/* Placeholder blocks (retention durations, sub-processor list, etc.) */}
                                {renderParagraphs(t(`privacy.section_${id}_placeholders`, { returnObjects: true, defaultValue: [] }))}
                                {/* Trailing paragraphs (used e.g. by the AI section for the "help vs. decide" note) */}
                                {renderParagraphs(t(`privacy.section_${id}_trailing`, { returnObjects: true, defaultValue: [] }))}
                            </section>
                        ))}

                        {/* Contact CTA */}
                        <section className="mt-14 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#3B82F6] text-white px-6 py-8 sm:px-8 sm:py-10">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <Mail className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] mb-2">
                                        {t('privacy.contact_cta_title')}
                                    </h3>
                                    <p className="text-white/85 text-sm sm:text-base">
                                        {t('privacy.contact_cta_desc')}
                                    </p>
                                    <p className="mt-3 text-white/70 text-sm font-mono" data-testid="privacy-contact-email">
                                        {t('privacy.contact_email_placeholder')}
                                    </p>
                                </div>
                            </div>
                        </section>
                    </article>
                </div>
            </div>

            {/* Minimal footer */}
            <footer className="py-8 px-4 sm:px-6 border-t border-[#E5E7EB] bg-white">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#6B7280]">
                    <div className="flex items-center gap-2">
                        <BrandLogo size={24} rounded="rounded" />
                        <span>{t('landing.footer_copyright')}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="hover:text-[#0A0A0A]">{t('privacy.back_home')}</Link>
                        <Link to="/about" className="hover:text-[#0A0A0A]">{t('privacy.footer_about')}</Link>
                        <Link to="/terms" className="hover:text-[#0A0A0A]">{t('privacy.footer_terms')}</Link>
                    </div>
                </div>
            </footer>

            <ScrollToTopButton />
        </div>
    );
}
